import {
  apiFileRequestTimeoutMs,
  apiRequestTimeoutMs,
  resourceAPIURL,
} from "../config";
import { useAuth } from "../contexts/AuthContext";
import { ApiError } from "../utils/apiError";
import { useAuthApi } from "./useAuthApi";

type FetchInit = RequestInit;

async function fetchWithTimeout(
  url: string,
  init: FetchInit,
  timeoutMs: number
): Promise<Response> {
  const controller = new AbortController();
  // timeoutMs <= 0 (or non-finite) means uncapped — no abort timer, so very
  // long-running calls (e.g. bulk backfill) aren't cut off client-side.
  const hasTimeout = Number.isFinite(timeoutMs) && timeoutMs > 0;
  const timeoutId = hasTimeout
    ? window.setTimeout(() => controller.abort(), timeoutMs)
    : undefined;
  const callerSignal = init.signal ?? undefined;

  if (callerSignal) {
    if (callerSignal.aborted) {
      controller.abort();
    } else {
      callerSignal.addEventListener("abort", () => controller.abort(), {
        once: true,
      });
    }
  }

  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new Error(
        "Request timed out. Please check your connection and try again."
      );
    }
    throw err;
  } finally {
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
  }
}

export default function useApiHelper(
  config: {
    apiUrl?: string;
    headers?: Record<string, string>;
    options?: RequestInit;
    timeoutMs?: number;
    fileTimeoutMs?: number;
  } = { apiUrl: undefined, headers: {}, options: {} }
) {
  const { token, loggedIn, loading } = useAuth();
  const { getToken } = useAuthApi();
  const baseUrl = config.apiUrl || `${resourceAPIURL}/api`;
  const requestTimeoutMs = config.timeoutMs ?? apiRequestTimeoutMs;
  const fileRequestTimeoutMs = config.fileTimeoutMs ?? apiFileRequestTimeoutMs;

  const _fetch = (url: string, init: FetchInit, timeoutMs = requestTimeoutMs) =>
    fetchWithTimeout(url, init, timeoutMs);

  const _getBearer = async () => {
    try {
      if (!loading && loggedIn) {
        return { Authorization: `Bearer ${token}` };
      } else {
        return { Authorization: `Bearer` };
      }
    } catch (err) {
      console.error(err);
      return { Authorization: `Bearer` };
    }
  };

  const _getReqOpts = async () => {
    // console.log("API Request to:", baseUrl);
    // console.log("Request Headers:", config.headers);

    const reqOpts = {
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
        ...(await _getBearer()),
        "x-page-url": typeof window !== 'undefined' && window.location ? window.location.href : 'n/a',
        ...config.headers,
      },
      ...config.options,
    };

    return reqOpts;
  };

  async function _handleResponse<T>(res: Response): Promise<T> {
    if (!res.ok) {
      let message = `HTTP Error: ${res.status}`;

      // 400 and 409 both carry a DefaultResponse whose message explains the refusal
      // in words a user can act on ("3 parts still on the vehicle…"). Without this
      // the caller only sees "HTTP Error: 409" and reports a generic save failure.
      // The messages inbox also explains its 403 (feature not switched on), 502 (eBay
      // didn't answer), 404 (conversation or order gone) and 503 (lock service down)
      // the same way; a body that isn't JSON (a gateway error page, say) just keeps
      // the generic message. Branch on the ApiError's status, not this text.
      if (res.status === 400 || res.status === 403 || res.status === 404 || res.status === 409 || res.status === 502 || res.status === 503) {
        try {
          let json = await res.json();
          if (json.message) {
            message = json.message;
          }
        } catch { }
      }

      if (res.status === 401) {
        console.log("%c [Auth] - 401 refreshing", "color: #f0c002");
        getToken(true, true);
      }

      throw new ApiError(message, res.status);
    }

    let text = await res.text();

    try {
      return JSON.parse(text);
    } catch (err: any) {
      console.warn(text);
      throw new Error(err);
    }
  }

  async function get<T>(path: string) {
    let rawRes = await _fetch(`${baseUrl}${path}`, {
      ...(await _getReqOpts()),
      method: "GET",
    });

    return await _handleResponse<T>(rawRes);
  }

  async function getFile<T>(path: string) {
    let rawRes = await _fetch(
      `${baseUrl}${path}`,
      {
        ...(await _getReqOpts()),
        method: "GET",
      },
      fileRequestTimeoutMs
    );

    // A failed download used to come back as a Blob of the error body, which callers then handed
    // to createObjectURL and opened in a tab - so a 404 presented as a blob: URL containing the
    // words "Document not found" rather than as an error. Status is checked here for the same
    // reason _handleResponse checks it: an error is never a file.
    if (!rawRes.ok) {
      let message = `HTTP Error: ${rawRes.status}`;

      try {
        let json = await rawRes.json();
        if (json.message) {
          message = json.message;
        }
      } catch { }

      if (rawRes.status === 401) {
        console.log("%c [Auth] - 401 refreshing", "color: #f0c002");
        getToken(true, true);
      }

      throw new ApiError(message, rawRes.status);
    }

    return rawRes.blob();
  }

  async function postGetFile<T>(path: string, body: T) {
    let rawRes = await _fetch(
      `${baseUrl}${path}`,
      {
        ...(await _getReqOpts()),
        body: JSON.stringify(body),
        method: "POST",
      },
      fileRequestTimeoutMs
    );

    return rawRes.blob();
  }

  async function put<T = any, U = any>(path: string, body: T) {
    let rawRes = await _fetch(`${baseUrl}${path}`, {
      ...(await _getReqOpts()),
      body: JSON.stringify(body),
      method: "PUT",
    });

    let res = await _handleResponse<U>(rawRes);

    return res;
  }

  async function patch<T>(path: string, body: Object) {
    let rawRes = await _fetch(`${baseUrl}${path}`, {
      ...(await _getReqOpts()),
      body: JSON.stringify(body),
      method: "PATCH",
    });

    let res = await _handleResponse<T>(rawRes);

    return res;
  }

  async function post<T = any, U = any>(path: string, body: T, signal?: AbortSignal) {
    let rawRes = await _fetch(`${baseUrl}${path}`, {
      ...(await _getReqOpts()),
      body: JSON.stringify(body),
      method: "POST",
      signal,
    });

    let res = await _handleResponse<U>(rawRes);

    return res;
  }

  async function postRaw<T>(path: string, body: T) {
    let rawRes = await _fetch(`${baseUrl}${path}`, {
      ...(await _getReqOpts()),
      body: JSON.stringify(body),
      method: "POST",
    });

    return rawRes;
  }

  async function postDocs<T>(path: string, body: any) {
    let rawRes = await _fetch(
      `${baseUrl}${path}`,
      {
        body: body,
        headers: {
          ...(await _getBearer()),
          "x-page-url": typeof window !== 'undefined' && window.location ? window.location.href : 'n/a',
          ...config.headers,
        },
        ...config.options,
        method: "POST",
      },
      fileRequestTimeoutMs
    );

    let res = await _handleResponse<T>(rawRes);

    return res;
  }

  async function del<T>(path: string, body?: any) {
    let rawRes = await _fetch(`${baseUrl}${path}`, {
      ...(await _getReqOpts()),
      body: JSON.stringify(body),
      method: "DELETE",
    });

    let res = await _handleResponse<T>(rawRes);

    return res;
  }

  async function customerPostFormData<T>(path: string, body: any) {
    let rawRes = await _fetch(
      `${baseUrl}${path}`,
      {
        method: "POST",
        headers: {
          "x-emailId": body.emailId,
          "x-page-url": typeof window !== 'undefined' && window.location ? window.location.href : 'n/a',
          ...config.headers,
        },
        body: body.data,
      },
      fileRequestTimeoutMs
    );

    let res = await _handleResponse<T>(rawRes);

    return res;
  }

  async function customerPatch<T>(path: string, body: any) {
    let rawRes = await _fetch(`${baseUrl}${path}`, {
      headers: {
        "Content-Type": "application/json",
        "x-emailId": body.emailId,
        "x-page-url": typeof window !== 'undefined' && window.location ? window.location.href : 'n/a',
        ...config.headers,
      },
      body: JSON.stringify(body),
      method: "PATCH",
    });

    let res = await _handleResponse<T>(rawRes);

    return res;
  }

  return {
    get,
    getFile,
    put,
    patch,
    post,
    postRaw,
    postDocs,
    postGetFile,
    customerPostFormData,
    customerPatch,
    del,
  };
}